import React, { useState, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { Auth0Provider } from '@auth0/auth0-react';
import { store } from './store/store';
import AppRoutes from './components/routes/AppRoutes';
import AuthManager from './components/auth/AuthManager';
import { ToastProvider } from './contexts/ToastContext';
import { getAuth0Config, auth0Config } from './config/auth0Config';
import SessionExpirationModal from './components/SessionExpirationModal';
import { useSessionMonitor } from './hooks/useSessionMonitor';
import LoadingScreen from './components/ui/LoadingScreen';
import ErrorBoundary from './components/common/ErrorBoundary';

function AppContent() {
  const { remainingSeconds, showWarning, handleContinue, handleDismiss } = useSessionMonitor();

  return (
    <>
      <AuthManager>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthManager>
      {showWarning && (
        <SessionExpirationModal
          remainingSeconds={remainingSeconds}
          onContinue={handleContinue}
          onDismiss={handleDismiss}
        />
      )}
    </>
  );
}

function App() {
  const [authConfig, setAuthConfig] = useState(() => {
    if (auth0Config.domain && auth0Config.clientId) {
      return auth0Config;
    }
    return null;
  });
  const [configError, setConfigError] = useState(null);

  useEffect(() => {
    if (authConfig) return;
    
    getAuth0Config()
      .then(setAuthConfig)
      .catch((error) => {
        setConfigError(error);
      });
  }, [authConfig]);

  if (configError) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h1>Configuration Error</h1>
        <p>Failed to load Auth0 configuration: {configError.message}</p>
        {/* <p>Please check that comphub-bizapi is running and accessible.</p> */}
      </div>
    );
  }

  if (!authConfig) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <LoadingScreen />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <Auth0Provider
          domain={authConfig.domain}
          clientId={authConfig.clientId}
          authorizationParams={authConfig.authorizationParams}
          useRefreshTokens={authConfig.useRefreshTokens}
          cacheLocation={authConfig.cacheLocation}
        >
          <ErrorBoundary>
            <ToastProvider>
              <AppContent />
            </ToastProvider>
          </ErrorBoundary>
        </Auth0Provider>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
